import { CheckIcon, EditIcon, StackIcon, TicketIcon, TrashIcon } from "@xola/icons";
import { Badge, flash, Popover, SubmitButton, Tooltip } from "@xola/ui-kit";
import { getUserLocale } from "get-user-locale";
import pluralize from "pluralize";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { AddOn, AmountType, Experience, UnitType, VariableAmountType } from "types";
import { CatalogItemImage } from "../../../Common/CatalogItemImage";
import { Currency } from "../../../Common/Currency";
import { NoTranslate } from "../../../Common/NoTranslate";
import { SecondaryButton } from "../../../Common/SecondaryButton";
import { Loader } from "../../../Loader";
import { DeleteCatalogItemModal, DeleteConflictError } from "../DeleteCatalogItemModal";
import { EditAddOnModal } from "./EditAddOnModal";
import { api } from "@/api/api";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { getAddOnPriceRange } from "@/models/CatalogItem";

const AddOnExperienceList = ({ addOn, experiences }: { addOn: AddOn; experiences?: Experience[] }) => {
    if (!experiences) {
        return null;
    }

    return (
        <ul className="space-y-1 p-2" data-testid="add-on-listing-label">
            {experiences.map((experience) => {
                const catalogItem = experience.catalog?.items?.find((item) => item.parent?.id === addOn.id);
                return (
                    <li key={experience.id}>
                        {catalogItem?.overrideParent ? (
                            <>
                                <EditIcon /> {experience.name}
                            </>
                        ) : (
                            <>
                                <CheckIcon /> {experience.name}
                            </>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};

const getAssociatedExperiences = (addOn: AddOn, experiences: Experience[]) => {
    return experiences.filter((experience) =>
        experience.catalog.items.some((catalogItem) => catalogItem.parent?.id === addOn.id),
    );
};

export const AddOnPriceRange = ({ addOn }: { addOn: AddOn }) => {
    const { price, priceType } = getAddOnPriceRange(addOn);
    const userLocale = getUserLocale();
    if (priceType === VariableAmountType.VARIABLE) {
        return <span data-testid="addon-item-price-varies">Price varies</span>;
    }

    if (price.lowest === price.highest) {
        return priceType === AmountType.ABSOLUTE ? (
            <Currency locale={userLocale}>{price.lowest}</Currency>
        ) : (
            <span data-testid="addon-item-price-percent">{price.lowest}%</span>
        );
    }

    return priceType === AmountType.ABSOLUTE ? (
        <span data-testid="addon-item-price-lowest-highest-absolute">
            <Currency locale={userLocale}>{price.lowest}</Currency> -{" "}
            <Currency locale={userLocale}>{price.highest}</Currency>
        </span>
    ) : (
        <span data-testid="addon-item-price-lowest-highest-percent">
            {price.lowest}% - {price.highest}%
        </span>
    );
};

export const AddOnItem = ({ addOn, experience }: { addOn: AddOn; experience?: Experience }) => {
    const invalidate = useInvalidate();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [deleteConflictError, setDeleteConflictError] = useState<DeleteConflictError>();
    const { data: experiences, isLoading } = useAutoFetchCollection<Experience>("experiences");
    const associatedExperiences = getAssociatedExperiences(addOn, experiences);

    const { mutateAsync: deleteCatalogItem, isLoading: isDeletingAddon } = useMutation(
        async ({ id, cascade }: { id: ID; cascade: boolean }) => {
            const url = `/catalogItems/${id}${cascade ? "?cascade=true" : ""}`;
            return api.delete(url);
        },
    );

    const toggleConfirmDeleteModal = () => {
        setShowDeleteModal(!showDeleteModal);
        setDeleteConflictError(undefined);
    };

    const handleDeleteCatalogItem = async (cascade = false) => {
        try {
            await deleteCatalogItem({ id: addOn.id, cascade });
            flash.show({ text: `Add-on removed`, color: "success" });
            invalidate("catalogItems", addOn.id);
            if (experience) {
                invalidate("experiences", experience.id);
            }
            invalidate("forms");
        } catch (error: any) {
            const deleteError = error?.response?.data?.field ?? {};
            const { reason, conflictingData, message } = deleteError;
            setDeleteConflictError({ reason, conflictingData, message });
            setShowDeleteModal(true);
        }
    };

    return (
        <>
            <div className="flex w-full justify-between" data-testid="addon-item">
                <div className="flex items-center space-x-2 py-4">
                    <CatalogItemImage size="small" catalogItem={addOn} />
                    <NoTranslate className="text-md font-bold" data-testid="addon-name">
                        {addOn.name}
                    </NoTranslate>
                    {addOn.overrideParent && (
                        <Tooltip
                            content="This add-on was customized for this product and may not be in sync with the global add-on"
                            placement="bottom"
                            maxWidth={200}
                        >
                            <Badge size="medium" color="warning" className="ml-4">
                                Overridden
                            </Badge>
                        </Tooltip>
                    )}
                </div>

                <div className="flex items-center space-x-6">
                    {isLoading ? (
                        <Loader size="tiny" />
                    ) : experience ? (
                        <Badge size="medium" icon={<StackIcon />} color="secondary">
                            <AddOnPriceRange addOn={addOn} />
                        </Badge>
                    ) : (
                        <Popover
                            className={associatedExperiences.length > 0 ? null : "hidden"}
                            content={<AddOnExperienceList addOn={addOn} experiences={associatedExperiences} />}
                        >
                            <Badge size="medium" icon={<TicketIcon />} color="secondary">
                                <div>
                                    <var data-var="experiencesCount">{associatedExperiences.length} </var>{" "}
                                    {pluralize("product", associatedExperiences.length)}
                                </div>
                            </Badge>
                        </Popover>
                    )}

                    <div className="actions flex items-center space-x-1.5">
                        <SecondaryButton
                            size="small"
                            data-testid="addon-item-edit-button"
                            onClick={() => setShowEditModal(true)}
                        >
                            <EditIcon />
                        </SecondaryButton>
                        <SubmitButton
                            isLoading={isDeletingAddon}
                            data-testid="addon-list-remove-button"
                            variant="outline"
                            color="secondary"
                            size="small"
                            onClick={async () => handleDeleteCatalogItem(false)}
                        >
                            <TrashIcon />
                        </SubmitButton>
                    </div>
                </div>
            </div>
            <EditAddOnModal
                isOpen={showEditModal}
                addOn={addOn}
                associatedExperiences={associatedExperiences}
                experience={experience}
                onClose={() => setShowEditModal(false)}
            />
            <DeleteCatalogItemModal
                isOpen={showDeleteModal}
                unitType={UnitType.ADDON}
                conflictError={deleteConflictError}
                onDelete={async () => handleDeleteCatalogItem(true)}
                onClose={toggleConfirmDeleteModal}
            />
        </>
    );
};
